import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useUserData } from "@/hooks/useUserData";
import type { MovieData } from "@/components/SwipeCard";
import TmdbAttribution from "@/components/TmdbAttribution";

interface BookmarkItem extends MovieData { media_type?: string; }

const Bookmarks = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getBookmarks, removeBookmark, getWatchHistory, addToWatchHistory } = useUserData();
  const [items, setItems] = useState<BookmarkItem[]>([]);
  const [watchedIds, setWatchedIds] = useState<Set<number>>(new Set());
  const [mediaFilter, setMediaFilter] = useState<"movie" | "tv">("movie");
  const [selectedMovie, setSelectedMovie] = useState<BookmarkItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BookmarkItem | null>(null);
  const [loading, setLoading] = useState(true);

  const sentiments = [
    { key: "hated" as const, emoji: "😡", label: t("watchHistory.hatedIt") },
    { key: "not_great" as const, emoji: "😕", label: t("watchHistory.notGreat") },
    { key: "ok" as const, emoji: "😐", label: t("watchHistory.ok") },
    { key: "pretty_good" as const, emoji: "😊", label: t("watchHistory.prettyGood") },
    { key: "loved" as const, emoji: "😍", label: t("watchHistory.lovedIt") },
  ];

  useEffect(() => {
    Promise.all([getBookmarks(), getWatchHistory()])
      .then(([bookmarks, history]) => { setItems(bookmarks); setWatchedIds(new Set(history.map((h: any) => h.id))); })
      .catch(console.error).finally(() => setLoading(false));
  }, [getBookmarks, getWatchHistory]);

  const filtered = items.filter((i) => (i.media_type || "movie") === mediaFilter);

  const handleRemove = async (movie: BookmarkItem) => {
    await removeBookmark(movie.id);
    setItems((prev) => prev.filter((i) => i.id !== movie.id));
    setDeleteTarget(null);
  };

  const handleRate = async (movie: BookmarkItem, rating: "loved" | "pretty_good" | "ok" | "not_great" | "hated") => {
    await addToWatchHistory(movie, rating, false, movie.media_type || "movie");
    await removeBookmark(movie.id);
    setItems((prev) => prev.filter((i) => i.id !== movie.id));
    setWatchedIds((prev) => new Set(prev).add(movie.id));
    setSelectedMovie(null);
  };

  const isWatched = (id: number) => watchedIds.has(id);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => { const el = scrollRef.current; if (!el) return; setCanScrollLeft(el.scrollLeft > 4); setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4); };
  useEffect(() => { checkScroll(); const el = scrollRef.current; if (el) el.addEventListener("scroll", checkScroll, { passive: true }); return () => el?.removeEventListener("scroll", checkScroll); }, [filtered]);
  const scroll = (dir: "left" | "right") => { const el = scrollRef.current; if (!el) return; el.scrollBy({ left: dir === "left" ? -el.clientWidth * 0.8 : el.clientWidth * 0.8, behavior: "smooth" }); };

  return (
    <div className="h-[100dvh] bg-background flex flex-col pb-14" style={{ paddingBottom: "calc(3.5rem + env(safe-area-inset-bottom, 0px))" }}>
      <div className="flex-shrink-0 px-4 pt-6 pb-4">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
          <button onClick={() => navigate("/home")} className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center active:scale-95 transition-transform">
            <ChevronLeft className="w-5 h-5 text-foreground" />
          </button>
          <div>
            <h1 className="font-display text-xl font-bold text-foreground">{t("bookmarks.title")}</h1>
            <p className="text-xs text-muted-foreground">{t("bookmarks.savedTitles", { count: items.length })}</p>
          </div>
        </motion.div>
      </div>

      <div className="flex-shrink-0 px-4 mb-4">
        <div className="flex gap-3">
          {([{ type: "movie" as const, emoji: "🎬", label: t("common.movies") }, { type: "tv" as const, emoji: "📺", label: t("common.tvShows") }]).map((opt) => (
            <button key={opt.type} onClick={() => setMediaFilter(opt.type)} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all active:scale-95 ${mediaFilter === opt.type ? "border-primary bg-primary/10" : "border-border bg-secondary hover:border-muted-foreground/30"}`}>
              <span className="text-xl">{opt.emoji}</span>
              <span className={`text-sm font-medium ${mediaFilter === opt.type ? "text-primary" : "text-muted-foreground"}`}>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="px-4 mt-4"><div className="rounded-xl bg-secondary/50 border border-border p-8 text-center"><p className="text-3xl mb-2">🏷️</p><p className="text-sm text-muted-foreground">{t("bookmarks.noBookmarks", { type: mediaFilter === "tv" ? "TV" : t("common.movie").toLowerCase() })}</p></div></div>
        ) : (
          <div className="pb-6">
            <div className="flex items-center justify-between px-4 mb-3">
              <h3 className="font-display text-base font-semibold text-foreground">{t("bookmarks.saved")}<span className="ml-2 text-xs text-muted-foreground font-normal">({filtered.length})</span></h3>
              {filtered.length > 4 && (
                <div className="flex items-center gap-1">
                  <button onClick={() => scroll("left")} disabled={!canScrollLeft} className={`w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 ${canScrollLeft ? "bg-secondary hover:bg-secondary/80 text-foreground" : "opacity-0 pointer-events-none"}`}><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={() => scroll("right")} disabled={!canScrollRight} className={`w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 ${canScrollRight ? "bg-secondary hover:bg-secondary/80 text-foreground" : "opacity-0 pointer-events-none"}`}><ChevronRight className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            <div ref={scrollRef} className="flex gap-3 overflow-x-auto px-4 pb-2" style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}>
              {filtered.map((movie, i) => (
                <motion.div key={movie.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="flex-shrink-0 w-[calc(25%-9px)] relative group">
                  <button onClick={() => { if (isWatched(movie.id)) setDeleteTarget(movie); else setSelectedMovie(movie); }} className="w-full text-left">
                    <div className="aspect-[2/3] rounded-lg overflow-hidden border border-border mb-1.5 relative">
                      <img src={movie.poster} alt={movie.title} className="w-full h-full object-cover" />
                      {isWatched(movie.id) && <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-primary/90 text-primary-foreground">{t("bookmarks.watched")}</div>}
                    </div>
                    <p className="text-xs text-foreground truncate font-medium">{movie.title}</p>
                    <p className="text-[10px] text-muted-foreground">{movie.year}</p>
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        <TmdbAttribution className="pt-6 pb-2" />
        <p className="text-[10px] text-muted-foreground/60 text-center mb-4">© 2026 Ayeris.tv — All rights reserved.</p>
      </div>

      {/* Delete confirmation */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-6" onClick={() => setDeleteTarget(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ type: "spring", stiffness: 300, damping: 25 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-card border border-border rounded-2xl p-6 space-y-4 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-18 flex-shrink-0 rounded-lg overflow-hidden border border-border"><img src={deleteTarget.poster} alt={deleteTarget.title} className="w-full h-full object-cover" /></div>
                <div className="flex-1 min-w-0"><p className="font-display font-bold text-foreground truncate">{deleteTarget.title}</p><p className="text-sm text-muted-foreground">{deleteTarget.year}</p></div>
              </div>
              <p className="text-sm text-muted-foreground text-center">{t("bookmarks.removeConfirm")}</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteTarget(null)} className="flex-1 py-3 rounded-xl border border-border text-sm font-medium text-foreground hover:bg-secondary transition-all active:scale-[0.98]">{t("common.cancel")}</button>
                <button onClick={() => handleRemove(deleteTarget)} className="flex-1 py-3 rounded-xl bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 transition-all active:scale-[0.98]">{t("common.remove")}</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rate dialog */}
      <AnimatePresence>
        {selectedMovie && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-6" onClick={() => setSelectedMovie(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ type: "spring", stiffness: 300, damping: 25 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-card border border-border rounded-2xl p-6 space-y-5 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="w-14 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-border"><img src={selectedMovie.poster} alt={selectedMovie.title} className="w-full h-full object-cover" /></div>
                <div className="flex-1 min-w-0"><p className="font-display font-bold text-foreground text-lg truncate">{selectedMovie.title}</p><p className="text-sm text-muted-foreground">{selectedMovie.year}</p></div>
                <button onClick={() => setSelectedMovie(null)} className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center"><X className="w-4 h-4 text-muted-foreground" /></button>
              </div>
              <p className="text-sm text-muted-foreground text-center">{t("bookmarks.rateToMove")}</p>
              <div className="grid grid-cols-5 gap-2">
                {sentiments.map((s) => (
                  <button key={s.key} onClick={() => handleRate(selectedMovie, s.key)} className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-border bg-secondary hover:border-primary/50 active:scale-95 transition-all">
                    <span className="text-3xl">{s.emoji}</span>
                    <span className="text-xs font-medium text-foreground">{s.label}</span>
                  </button>
                ))}
              </div>
              <button onClick={() => { setSelectedMovie(null); setDeleteTarget(selectedMovie); }} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-secondary transition-all active:scale-[0.98]">
                <Trash2 className="w-4 h-4" /> {t("bookmarks.removeBookmark")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Bookmarks;
